/**
 * <ActivityStatement> — l'énoncé d'une activité (organism).
 *
 * Ce que le legacy appelle le « matériel » : la consigne écrite, et ce qui l'accompagne — une
 * fiche d'exercice, un PDF, un lien. `MaterialDisplayModal` route sur trois formes (texte, image
 * ou PDF, URL) ; ici les trois cohabitent, parce qu'une activité peut porter une consigne ET une
 * pièce, et que le parent n'a aucune raison de basculer entre elles.
 *
 * Rien à posséder : tout arrive en props. Le visualiseur appartient à `<ActivityMediaList>`.
 *
 * La consigne passe par `<RichText>`, pas par `<Text>` : un énoncé porte du markdown et des
 * formules, et le parent doit lire la même chose que son enfant — une fraction composée, pas
 * `$\\frac{3}{4}$`.
 *
 * Deux props pour l'élève qui fait l'exercice (absentes : la fiche du parent, inchangée) :
 * `mediaLayout="full"` pose les images en grand plutôt qu'en vignettes (et au web, les pages d'un PDF), et `figurePending` tient la
 * place d'une figure que le serveur dessine encore (l'ancien code la sonde 5 s par 5 s pendant une
 * minute, puis se tait).
 *
 * `reading` est la lecture de l'élève qui lit pour lui-même (absente : la feuille standard, le parent,
 * le tuteur) — l'ancien `EnonceReader`. Sur un énoncé écrit seulement (une photo, un PDF n'en ont
 * pas) : la police et la taille de sa lecture, la lecture adaptée (l'encadré « À utiliser », des lignes
 * d'environ 65 caractères, l'énoncé resserré au centre), et « Aa » sous le texte, qui ouvre la fiche
 * « Ma lecture ». L'énoncé se lit toujours en entier, ses questions à la suite.
 *
 * `captionCut`, pour le centre du live du masteur (absent : la consigne en entier, partout ailleurs) : sur
 * un énoncé non écrit (une photo, un PDF, un lien), le texte qui l'accompagne est un sous-titre — en gras,
 * centré, coupé au-delà de 100 caractères et déplié au toucher (`statementCaption`, l'ancien
 * `MaterialDisplayModal` en `noModal`). Déplié ou non est un état d'interface : il vit ici, et se replie
 * quand le texte change.
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import {
    type UIActivityStatement,
    type UIEnonceReading,
    type UIStatementReading,
} from "../../../ports/primary/ui/types"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { adaptedLineWidth, RichText, withDataBox } from "../../molecules/RichText"
import { ActivityMediaList, type ActivityMediaLayout } from "../ActivityMediaList"
import { StatementFigure } from "../../molecules/StatementFigure"
import { statementCaption } from "./statementCaption"

/** La place d'une figure : celle d'une fiche en paysage, à la largeur d'un téléphone. */
const FIGURE_PLACEHOLDER_HEIGHT = 200

const TEXTS = {
    figurePending: "Génération de la figure…",
    reading: "Ma lecture : régler l'affichage de l'énoncé",
    unfold: "Afficher tout le texte",
    fold: "Réduire le texte",
}

export type ActivityStatementProps = {
    statement: UIActivityStatement
    /** Les pièces de l'énoncé : en vignettes (défaut) ou en grand. */
    mediaLayout?: ActivityMediaLayout
    /** Une figure est annoncée et pas encore là : sa forme, sous la consigne. Défaut `false`. */
    figurePending?: boolean
    /** Un PDF de l'énoncé ne s'est pas chargé. Absent : rien. */
    onPdfError?: (cause: unknown) => void
    /** La lecture de l'élève qui lit pour lui-même. Absente : la feuille standard, sans « Aa ». */
    reading?: UIStatementReading
    /**
     * Sur un énoncé non écrit (une photo, un PDF, un lien), le texte qui l'accompagne en sous-titre, coupé
     * au-delà de 100 caractères et déplié au toucher (le centre du live du masteur). Défaut `false` : la
     * consigne en entier.
     */
    captionCut?: boolean
    testID?: string
}

export function ActivityStatement(props: ActivityStatementProps) {
    const { statement, mediaLayout, figurePending = false, onPdfError, reading, captionCut = false, testID } = props
    // Le texte déplié : celui-ci, pas un autre (un autre énoncé arrive replié).
    const [unfolded, setUnfolded] = useState<string | null>(null)

    const hasText = statement.text != null && statement.text !== ""
    const figure = statement.figureSvg != null && statement.figureSvg !== "" ? statement.figureSvg : null
    if (!hasText && statement.media.length === 0 && figure == null) return null

    // La lecture ne vaut que pour un énoncé écrit : une photo ou un PDF se lisent tels quels.
    const read = hasText ? reading : undefined
    // L'ancien `materialType` : une adresse ou des pièces jointes font un énoncé non écrit.
    const caption =
        captionCut && hasText && statement.media.length > 0
            ? statementCaption(statement.text!, unfolded === statement.text)
            : null

    return (
        <Stack gap="3" testID={testID} style={read?.reading.adapted ? styles.adapted(read.reading) : undefined}>
            <SectionLabel>Énoncé</SectionLabel>
            {caption != null && (
                <Pressable
                    onPress={
                        caption.foldable
                            ? () => setUnfolded(unfolded === statement.text ? null : statement.text!)
                            : undefined
                    }
                    disabled={!caption.foldable}
                    accessibilityRole={caption.foldable ? "button" : undefined}
                    accessibilityHint={
                        caption.foldable ? (unfolded === statement.text ? TEXTS.fold : TEXTS.unfold) : undefined
                    }
                >
                    <Text weight="bold" color="text-muted" center>
                        {caption.text}
                    </Text>
                </Pressable>
            )}
            {hasText && caption == null && (
                <View>
                    <RichText
                        value={read?.reading.adapted ? withDataBox(statement.text!) : statement.text!}
                        reading={read?.reading}
                    />
                    {read != null && (
                        <View style={styles.readingButton}>
                            <IconButton
                                iconName="font"
                                appearance="transparent"
                                iconColor={read.custom ? "primary" : "text-muted"}
                                size={44}
                                iconSize={22}
                                onPress={read.onOpen}
                                accessibilityLabel={TEXTS.reading}
                            />
                        </View>
                    )}
                </View>
            )}
            {figure != null && <StatementFigure svg={figure} />}
            {figurePending && (
                <Stack gap="2">
                    <DelayedSkeleton>
                        <Skeleton shape="block" height={FIGURE_PLACEHOLDER_HEIGHT} />
                    </DelayedSkeleton>
                    <Text size="text-sm" color="text-muted" center>
                        {TEXTS.figurePending}
                    </Text>
                </Stack>
            )}
            <ActivityMediaList media={statement.media} mediaLayout={mediaLayout} onPdfError={onPdfError} />
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    /** En lecture adaptée, des lignes d'environ 65 caractères : l'énoncé se resserre au centre. */
    adapted: (reading: UIEnonceReading) => ({
        width: "100%" as const,
        maxWidth: adaptedLineWidth(reading),
        alignSelf: "center" as const,
    }),
    /** « Aa » sous le texte, à droite, comme l'ancien. */
    readingButton: { alignItems: "flex-end" as const },
}))
